import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Zap,
  Settings,
  Cloud,
  Server,
  AlertTriangle,
  Database,
  Users,
  Loader2,
  CheckCircle2,
  Save,
  Plus,
  Edit2,
  X,
  ShieldCheck,
  Sparkles,
  FileText,
  KeyRound,
} from 'lucide-react';
import { useAuthStore } from '../stores/auth';
import { useToastStore } from '../stores/toast';
import { ADMIN_LABELS, ROLE_LABELS } from '../config/labels';
import {
  getModelConfigs, upsertModelConfig, testModelConfig, type ModelConfig, type UpsertModelConfigInput,
  getSystemSettings, updateSystemSetting,
  encryptExistingDocs, getPiiStripLock, type PiiStripLock,
  getDataSources, toggleDataSource, type DataSource,
  getAdminUsers, createUser, updateUser, type AdminUser, type CreateUserInput,
  getEgressLog, type EgressLogEntry, type EgressLogQuery,
  getLLMFieldConfig, updateLLMFieldConfig, type LLMFieldConfig,
} from '../api/admin';
import {
  getWorkflows, createWorkflow, updateWorkflow, addWorkflowStep,
  type WorkflowListItem,
} from '../api/workflow';
import RubricTab from '../components/admin/RubricTab';
import LearnedRulesTab from '../components/admin/LearnedRulesTab';
import RulesProfileTab from '../components/admin/RulesProfileTab';
import ClaudeActivationModal from '../components/admin/ClaudeActivationModal';
import PiiRulesTab from '../components/admin/PiiRulesTab';

type Tab = 'rubric' | 'learnedRules' | 'rulesProfile' | 'modelConfig' | 'dataSources' | 'users' | 'workflows' | 'egressLog' | 'piiRules';

const PATHS: Array<ModelConfig['path']> = ['public', 'private'];
const PROFILES: Array<ModelConfig['profile']> = ['dev', 'uat', 'prod'];

const PATH_LABELS: Record<string, string> = {
  public: ADMIN_LABELS.publicPath,
  private: ADMIN_LABELS.privatePath,
};
const PROFILE_LABELS: Record<string, string> = {
  dev: ADMIN_LABELS.devProfile,
  uat: ADMIN_LABELS.uatProfile,
  prod: ADMIN_LABELS.prodProfile,
};

const ROLE_BADGE_CLASSES: Record<string, string> = {
  analyst: 'bg-blue-100 text-blue-800',
  principal: 'bg-purple-100 text-purple-800',
  compliance: 'bg-amber-100 text-amber-800',
  operator: 'bg-indigo-100 text-indigo-800',
};

/* ---------- Guard rail check ---------- */
function isCloudBackendType(backendType: string): boolean {
  return backendType === 'cloud' || backendType === 'anthropic' || backendType === 'claude_code' || backendType === 'pi_agent';
}
// true when the private path is pointed at a hosted/cloud model that
// is NOT marked self-hosted (i.e. document text will leave our network).
function isPrivateHostedWarning(path: string, backendType: string, selfHosted?: boolean): boolean {
  return path === 'private' && isCloudBackendType(backendType) && !selfHosted;
}
function isAgenticBackendType(backendType: string): boolean {
  return backendType === 'claude_code' || backendType === 'pi_agent';
}

/* ============================================================
   Model Config Tab
   ============================================================ */
function ModelConfigTab() {
  const { addToast } = useToastStore();
  const [configs, setConfigs] = useState<ModelConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<UpsertModelConfigInput | null>(null);
  // tracks whether the user is actively typing a new API key (so the
  // masked view + Change/Remove buttons hide and the password input shows).
  const [keyEditing, setKeyEditing] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  // per-card Test-connection state. testingKey is the path:profile
  // being probed; testResults keyed by path:profile holds the last result.
  const [testingKey, setTestingKey] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { ok: boolean; message: string; latency_ms?: number | null }>>({});
  // LLM concurrency (admin-configurable llm_max_parallel)
  const [llmParallel, setLlmParallel] = useState<number>(4);
  const [llmInput, setLlmInput] = useState<string>('4');
  const [llmSaving, setLlmSaving] = useState(false);
  // Security: cloud PII-strip toggle + document-encryption status
  const [piiStrip, setPiiStrip] = useState<boolean | null>(null);
  const [piiStripSaving, setPiiStripSaving] = useState(false);
  // eval_public_path_only — force all learned-rules eval through the public
  // path using PII-stripped docs (skips the private path entirely).
  const [evalPublicOnly, setEvalPublicOnly] = useState<boolean | null>(null);
  const [evalPublicOnlySaving, setEvalPublicOnlySaving] = useState(false);
  // allow private document text to cloud LLM (opt-in, OFF default).

  // cloud_docs_mode ("drop" | "pii_stripped") — what document text
  // a cloud backend receives in chat. Cloud models never receive un-PII-
  // stripped document text.
  const [cloudDocsMode, setCloudDocsMode] = useState<string>('drop');
  const [cloudDocsModeSaving, setCloudDocsModeSaving] = useState(false);
  // ingest_stripping_mode (regex | ai_local | none) -- the DEFAULT PII
  // stripping applied to each document on INGEST (upload). Produces + stores
  // a PII-stripped version (Document.pii_stripped_text) the chat cloud-path
  // sends instead of re-scrubbing at chat time.
  const [ingestStrippingMode, setIngestStrippingMode] = useState<string>('regex');
  const [ingestStrippingSaving, setIngestStrippingSaving] = useState(false);
  // LLM field configuration (blocked fields)
  const [llmFields, setLlmFields] = useState<LLMFieldConfig[]>([]);
  const [llmFieldsLoading, setLlmFieldsLoading] = useState(false);
  const [llmFieldsSaving, setLlmFieldsSaving] = useState(false);
  const [showFieldConfig, setShowFieldConfig] = useState(false);
  // PII-strip guard lock. When the private path uses a cloud
  // backend, the toggle is forced ON + disabled (non-editable).
  const [piiStripLock, setPiiStripLock] = useState<PiiStripLock | null>(null);
  const [docEncEnabled, setDocEncEnabled] = useState<boolean>(false);
  const [encBusy, setEncBusy] = useState(false);
  // Claude agent activation modal.
  const [showClaudeActivation, setShowClaudeActivation] = useState(false);

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getModelConfigs();
      setConfigs(data);
      try {
        const settings = await getSystemSettings();
        const lp = settings.find((x) => x.key === 'llm_max_parallel');
        if (lp && lp.value_int) { setLlmParallel(lp.value_int); setLlmInput(String(lp.value_int)); }
        const pii = settings.find((x) => x.key === 'cloud_llm_pii_strip');
        if (pii) {
          setPiiStrip(pii.value_int != null ? !!pii.value_int : pii.value === 'true');
        }
        const epo = settings.find((x) => x.key === 'eval_public_path_only');
        if (epo) {
          setEvalPublicOnly(epo.value_int != null ? !!epo.value_int : epo.value === 'true');
        }
        const cdm = settings.find((x) => x.key === 'cloud_docs_mode');
        if (cdm && (cdm.value === 'drop' || cdm.value === 'pii_stripped')) {
          setCloudDocsMode(cdm.value);
        } else {
          setCloudDocsMode('drop');
        }
        const ism = settings.find((x) => x.key === 'ingest_stripping_mode');
        if (ism && (ism.value === 'regex' || ism.value === 'ai_local' || ism.value === 'none')) {
          setIngestStrippingMode(ism.value);
        } else {
          setIngestStrippingMode('regex');
        }
        const docEnc = settings.find((x) => x.key === 'doc_encryption_enabled');
        if (docEnc) {
          setDocEncEnabled(docEnc.value_int != null ? !!docEnc.value_int : docEnc.value === 'true');
        }
      } catch { /* non-fatal */ }
      try {
        const lock = await getPiiStripLock();
        setPiiStripLock(lock);
        // When locked, force the toggle ON regardless of the stored value
        // (the backend refuses to disable it; the UI must reflect that).
        if (lock.locked) setPiiStrip(true);
      } catch { /* non-fatal */ }
    } catch {
      addToast('Failed to load model configurations', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const getConfig = (path: string, profile: string): ModelConfig | undefined =>
    configs.find((c) => c.path === path && c.profile === profile);

  const startEdit = (path: string, profile: string) => {
    const existing = getConfig(path, profile);
    setEditing(`${path}:${profile}`);
    setEditForm({
      path: path as UpsertModelConfigInput['path'],
      profile: profile as UpsertModelConfigInput['profile'],
      backend_type: existing?.backend_type ?? 'local',
      endpoint: existing?.endpoint ?? '',
      model_name: existing?.model_name ?? '',
      api_key_ref: existing?.api_key_ref ?? '',
      self_hosted: existing?.self_hosted ?? false,
      // Do NOT prefill api_key (server keeps the secret). clear_api_key starts
      // false so we don't accidentally wipe an existing key on save.
      api_key: undefined,
      clear_api_key: false,
    });
    setKeyEditing(false);
    setSavedKey(null);
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditForm(null);
    setKeyEditing(false);
  };

  const handleSave = async () => {
    if (!editForm) return;
    const key = `${editForm.path}:${editForm.profile}`;
    // the private path hosted-model guard rail is now a soft warning,
    // not a hard block. The backend returns guard_rail_warning on save; we
    // surface it as a toast. Do NOT abort the save here.
    if (!editForm.model_name.trim()) {
      addToast('Model name is required', 'error');
      return;
    }
    setSaving(key);
    try {
      // Build a clean payload: drop empty/undefined api_key + clear_api_key so
      // we don't send stray flags that would wipe or no-op the stored key.
      const payload: UpsertModelConfigInput = { ...editForm };
      if (!payload.api_key) delete payload.api_key;
      if (!payload.clear_api_key) delete payload.clear_api_key;
      const saved = await upsertModelConfig(payload);
      await loadConfigs();
      addToast(ADMIN_LABELS.configSaved, 'success');
      // surface a guard-rail warning (private path -> hosted model)
      // returned by the backend as an info toast so the operator sees it.
      if (saved.guard_rail_warning) {
        addToast(saved.guard_rail_warning, 'error');
      }
      setSavedKey(key);
      setEditing(null);
      setEditForm(null);
      setKeyEditing(false);
      setTimeout(() => setSavedKey(null), 2000);
    } catch {
      addToast('Failed to save model configuration', 'error');
    } finally {
      setSaving(null);
    }
  };

  // test a model config (saved or in-progress edit) with a tiny LLM
  // ping. When isEditing, probe the editForm values so the operator can test
  // BEFORE saving; otherwise probe the saved cfg.
  const handleTestConfig = async (path: string, profile: string) => {
    const key = `${path}:${profile}`;
    const isEditing = editing === key && editForm;
    const input = isEditing
      ? { ...editForm!, api_key: editForm!.api_key || undefined, clear_api_key: undefined }
      : {
          path: path as UpsertModelConfigInput['path'],
          profile: profile as UpsertModelConfigInput['profile'],
          backend_type: (getConfig(path, profile)?.backend_type ?? 'local') as UpsertModelConfigInput['backend_type'],
          endpoint: getConfig(path, profile)?.endpoint ?? '',
          model_name: getConfig(path, profile)?.model_name ?? '',
          api_key_ref: getConfig(path, profile)?.api_key_ref ?? '',
          self_hosted: getConfig(path, profile)?.self_hosted ?? false,
        };
    if (!input.model_name?.trim()) {
      addToast('Model name is required to test', 'error');
      return;
    }
    setTestingKey(key);
    setTestResults((r) => ({ ...r, [key]: { ok: false, message: 'Testing…' } }));
    try {
      const res = await testModelConfig(input as UpsertModelConfigInput);
      setTestResults((r) => ({ ...r, [key]: res }));
      addToast(res.ok ? `Test OK (${res.latency_ms ?? '?'}ms): ${res.message}` : `Test failed: ${res.message}`, res.ok ? 'success' : 'error');
    } catch (e) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Request failed';
      setTestResults((r) => ({ ...r, [key]: { ok: false, message: msg } }));
      addToast(`Test failed: ${msg}`, 'error');
    } finally {
      setTestingKey(null);
    }
  };

  const handleSaveLlmParallel = async () => {
    const n = parseInt(llmInput, 10);
    if (!Number.isFinite(n) || n < 1) { addToast('Parallelism must be a positive integer', 'error'); return; }
    setLlmSaving(true);
    try {
      const updated = await updateSystemSetting('llm_max_parallel', n);
      setLlmParallel(updated.value_int ?? n);
      addToast(`AI parallelism set to ${updated.value_int ?? n}`, 'success');
    } catch { addToast('Failed to update AI parallelism', 'error'); }
    finally { setLlmSaving(false); }
  };

  const handleTogglePiiStrip = async (enabled: boolean) => {
    setPiiStrip(enabled);
    setPiiStripSaving(true);
    try {
      const updated = await updateSystemSetting('cloud_llm_pii_strip', enabled);
      const now = updated.value_int != null ? !!updated.value_int : updated.value === 'true';
      setPiiStrip(now);
      addToast(`Cloud AI PII redaction ${now ? 'enabled' : 'disabled'} (live)`, 'success');
    } catch {
      setPiiStrip(!enabled);
      addToast('Failed to update PII redaction setting', 'error');
    } finally {
      setPiiStripSaving(false);
    }
  };

  const handleToggleEvalPublicOnly = async (enabled: boolean) => {
    setEvalPublicOnly(enabled);
    setEvalPublicOnlySaving(true);
    try {
      const updated = await updateSystemSetting('eval_public_path_only', enabled);
      const now = updated.value_int != null ? !!updated.value_int : updated.value === 'true';
      setEvalPublicOnly(now);
      addToast(`Eval public-path-only ${now ? 'enabled' : 'disabled'} (live)`, 'success');
    } catch {
      setEvalPublicOnly(!enabled);
      addToast('Failed to update eval public-path-only setting', 'error');
    } finally {
      setEvalPublicOnlySaving(false);
    }
  };

  const handleChangeCloudDocsMode = async (mode: string) => {
    if (mode !== 'drop' && mode !== 'pii_stripped') return;
    setCloudDocsMode(mode);
    setCloudDocsModeSaving(true);
    try {
      const updated = await updateSystemSetting('cloud_docs_mode', mode);
      setCloudDocsMode(updated.value === 'pii_stripped' ? 'pii_stripped' : 'drop');
      addToast(`Cloud docs mode set to ${updated.value} (live)`, 'success');
    } catch {
      addToast('Failed to update cloud docs mode', 'error');
    } finally {
      setCloudDocsModeSaving(false);
    }
  };

  const handleChangeIngestStrippingMode = async (mode: string) => {
    if (mode !== 'regex' && mode !== 'ai_local' && mode !== 'none') return;
    setIngestStrippingMode(mode);
    setIngestStrippingSaving(true);
    try {
      const updated = await updateSystemSetting('ingest_stripping_mode', mode);
      const v = updated.value;
      setIngestStrippingMode(v === 'ai_local' || v === 'none' ? v : 'regex');
      addToast(`Ingest PII stripping set to ${updated.value} (live)`, 'success');
    } catch {
      addToast('Failed to update ingest PII stripping mode', 'error');
    } finally {
      setIngestStrippingSaving(false);
    }
  };

  const handleEncryptExisting = async () => {
    setEncBusy(true);
    try {
      const res = await encryptExistingDocs();
      addToast(`Encrypted ${res.encrypted}, skipped ${res.skipped}, errors ${res.errors}`, res.errors ? 'error' : 'success');
      // Refresh doc-enc status in case the key was just configured.
      await loadConfigs();
    } catch {
      addToast('Encrypt-existing failed (is APP_DOC_ENC_KEY set?)', 'error');
    } finally {
      setEncBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-primary-500" size={28} />
      </div>
    );
  }

  return (
    <div>
      {/* AI agent activation */}
      <div className="card p-4 mb-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <KeyRound className="text-primary-600 shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-sm font-semibold text-slate-900">AI Agent</p>
              <p className="text-xs text-slate-500 mt-1">
                Activate or reactivate the AI agent subscription (OAuth) used by
                the agentic bridge. No shell access required.
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowClaudeActivation(true)}
            className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
          >
            Activate / Reactivate
          </button>
        </div>
      </div>
      {showClaudeActivation && (
        <ClaudeActivationModal onClose={() => setShowClaudeActivation(false)} />
      )}

      {/* LLM concurrency control */}
      <div className="card p-4 mb-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <Zap className="text-primary-600 shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-sm font-semibold text-slate-900">AI Concurrency</p>
              <p className="text-xs text-slate-500 mt-1">
                Max parallel AI calls the system makes at once, system-wide. Applied live. Current: <span className="font-semibold text-primary-700">{llmParallel}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              value={llmInput}
              onChange={(e) => setLlmInput(e.target.value)}
              className="w-20 px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-1 focus:ring-primary-500"
              aria-label="AI parallel calls"
            />
            <button
              onClick={handleSaveLlmParallel}
              disabled={llmSaving}
              className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {llmSaving ? 'Saving…' : 'Set'}
            </button>
          </div>
        </div>
      </div>

      {/* Security: PII-strip toggle + document encryption status */}
      <div className="card p-4 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <ShieldCheck className="text-primary-600 shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-sm font-semibold text-slate-900">Security</p>
            <p className="text-xs text-slate-500 mt-1">
              Cloud-bound PII redaction + at-rest document encryption.
            </p>
          </div>
        </div>

        {/* Cloud AI PII redaction toggle */}
        <div className="flex items-center justify-between gap-4 py-3 border-t border-slate-100">
          <div className="pr-4">
            <p className="text-sm font-medium text-slate-800">Cloud AI PII redaction</p>
            <p className="text-xs text-slate-500 mt-1">
              When ON, DB-sourced PII is stripped before calls to a cloud AI
              (public path). The local AI is unaffected. Applied live.
            </p>
            {piiStripLock?.locked && (
              <p className="text-xs text-amber-700 mt-1 font-medium">
                Locked ON: required when the private path uses a cloud backend
                ({piiStripLock.private_backend_type}). Switch the private path
                to a local backend to disable.
              </p>
            )}
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={piiStrip ?? true}
            disabled={piiStripSaving || piiStrip === null || (piiStripLock?.locked ?? false)}
            onClick={() => handleTogglePiiStrip(!(piiStrip ?? true))}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
              (piiStrip ?? true) ? 'bg-primary-600' : 'bg-slate-300'
            }`}
            aria-label="Toggle cloud AI PII redaction"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                (piiStrip ?? true) ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Cloud docs mode (configurable: drop | pii_stripped) */}
        <div className="flex items-center justify-between gap-4 py-3 border-t border-slate-100">
          <div className="pr-4">
            <p className="text-sm font-medium text-slate-800">Cloud docs mode</p>
            <p className="text-xs text-slate-500 mt-1">
              Governs what document text a cloud AI receives in chat when
              documents are in scope. Cloud AI never receives un-redacted
              document text.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={cloudDocsMode}
              disabled={cloudDocsModeSaving}
              onChange={(e) => handleChangeCloudDocsMode(e.target.value)}
              className="text-sm border border-slate-300 rounded-md px-2 py-1 disabled:opacity-50"
              aria-label="Cloud docs mode"
            >
              <option value="drop">Drop documents (no document text to cloud AI)</option>
              <option value="pii_stripped">Send PII-redacted document text to cloud AI</option>
            </select>
          </div>
        </div>

        {/* Eval public-path-only toggle */}
        <div className="flex items-center justify-between gap-4 py-3 border-t border-slate-100">
          <div className="pr-4">
            <p className="text-sm font-medium text-slate-800">Eval public-path-only</p>
            <p className="text-xs text-slate-500 mt-1">
              When ON, learned-rules evaluation runs through the public path
              using PII-stripped document text, skipping the private path.
              Useful when the private-path model is unavailable or you want a
              single consistent eval path. Applied live.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={evalPublicOnly ?? false}
            disabled={evalPublicOnlySaving || evalPublicOnly === null}
            onClick={() => handleToggleEvalPublicOnly(!(evalPublicOnly ?? false))}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
              (evalPublicOnly ?? false) ? 'bg-primary-600' : 'bg-slate-300'
            }`}
            aria-label="Toggle eval public-path-only"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                (evalPublicOnly ?? false) ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Ingest PII stripping mode (configurable: regex | ai_local | none) */}
        <div className="flex items-center justify-between gap-4 py-3 border-t border-slate-100">
          <div className="pr-4">
            <p className="text-sm font-medium text-slate-800">Ingest PII stripping</p>
            <p className="text-xs text-slate-500 mt-1">
              The DEFAULT PII stripping applied to each document on upload. A
              PII-stripped version is stored and sent to the cloud AI in chat
              (when cloud docs mode is PII-stripped) instead of re-scrubbing at
              chat time. Operators/principals can override this per-drop on the
              package-drop page.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={ingestStrippingMode}
              disabled={ingestStrippingSaving}
              onChange={(e) => handleChangeIngestStrippingMode(e.target.value)}
              className="text-sm border border-slate-300 rounded-md px-2 py-1 disabled:opacity-50"
              aria-label="Ingest PII stripping mode"
            >
              <option value="regex">Pattern redaction (PII Rules)</option>
              <option value="ai_local">Local AI rewrite</option>
              <option value="none">No stripping on ingest</option>
            </select>
          </div>
        </div>

        {/* LLM Field Configuration — blocked fields */}
        <div className="flex items-center justify-between gap-4 py-3 border-t border-slate-100">
          <div className="pr-4">
            <p className="text-sm font-medium text-slate-800">LLM field blocking</p>
            <p className="text-xs text-slate-500 mt-1">
              Control which case-fact fields are never sent to AI during chat
              and review. Blocked fields are removed entirely — the AI never
              sees the field name or value. Applies to ALL models (local + hosted).
            </p>
          </div>
          <button
            onClick={async () => {
              if (llmFields.length === 0) {
                setLlmFieldsLoading(true);
                try {
                  const cfg = await getLLMFieldConfig();
                  setLlmFields(cfg.fields);
                } catch (e) {
                  addToast('Failed to load field config', 'error');
                } finally {
                  setLlmFieldsLoading(false);
                }
              }
              setShowFieldConfig(!showFieldConfig);
            }}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1"
          >
            {llmFieldsLoading ? <Loader2 size={15} className="animate-spin" /> : <Settings size={15} />}
            {showFieldConfig ? 'Hide' : 'Configure'} fields
          </button>
        </div>
        {showFieldConfig && llmFields.length > 0 && (
          <div className="py-3 border-t border-slate-100">
            <div className="grid grid-cols-2 gap-1.5 max-h-80 overflow-y-auto">
              {llmFields.map((f, i) => (
                <label
                  key={f.field}
                  className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:bg-slate-50 px-2 py-1 rounded"
                >
                  <input
                    type="checkbox"
                    checked={f.blocked}
                    onChange={(e) => {
                      const updated = [...llmFields];
                      updated[i] = { ...f, blocked: e.target.checked };
                      setLlmFields(updated);
                    }}
                    className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className={f.blocked ? 'text-red-600 font-medium' : ''}>
                    {f.field}
                  </span>
                </label>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-3">
              <button
                onClick={async () => {
                  setLlmFieldsSaving(true);
                  try {
                    const blocked = llmFields.filter(f => f.blocked).map(f => f.field);
                    await updateLLMFieldConfig(blocked);
                    addToast(`${blocked.length} fields blocked from AI`, 'success');
                  } catch (e) {
                    addToast('Failed to save field config', 'error');
                  } finally {
                    setLlmFieldsSaving(false);
                  }
                }}
                disabled={llmFieldsSaving}
                className="inline-flex items-center gap-1 text-sm bg-primary-600 text-white px-3 py-1.5 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {llmFieldsSaving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                Save configuration
              </button>
              <span className="text-xs text-slate-400">
                {llmFields.filter(f => f.blocked).length} blocked / {llmFields.length} total
              </span>
            </div>
          </div>
        )}

        {/* Document encryption status (read-only) */}
        <div className="flex items-center justify-between gap-4 py-3 border-t border-slate-100">
          <div className="pr-4">
            <p className="text-sm font-medium text-slate-800">Document encryption at rest</p>
            <p className="text-xs text-slate-500 mt-1">
              Configured via the APP_DOC_ENC_KEY secret. When enabled, uploaded
              documents are stored encrypted on disk. The key is never exposed
              here. Set APP_DOC_ENC_KEY to enable.
            </p>
          </div>
          <span
            className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
              docEncEnabled ? 'bg-green-100 text-green-800' : 'bg-slate-200 text-slate-600'
            }`}
          >
            {docEncEnabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>

        {/* Encrypt existing documents action */}
        <div className="flex items-center justify-between gap-4 py-3 border-t border-slate-100">
          <div className="pr-4">
            <p className="text-sm font-medium text-slate-800">Encrypt existing documents</p>
            <p className="text-xs text-slate-500 mt-1">
              Encrypts already-uploaded (plaintext) documents in place. Idempotent
              — files already encrypted are skipped. Operator-only; audited.
            </p>
          </div>
          <button
            type="button"
            onClick={handleEncryptExisting}
            disabled={!docEncEnabled || encBusy}
            className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
            title={docEncEnabled ? 'Encrypt existing uploads in place' : 'Document encryption is disabled (set APP_DOC_ENC_KEY)'}
          >
            {encBusy ? 'Working…' : 'Encrypt existing'}
          </button>
        </div>
      </div>

      {/* Guard rail banner */}
      <div className="card p-4 mb-6 border-amber-200 bg-amber-50">
        <div className="flex items-start gap-3">
          <ShieldCheck className="text-amber-600 shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-sm font-semibold text-amber-900">Data Security Policy</p>
            <p className="text-xs text-amber-700 mt-1">
              The private processing path must use a local backend in UAT and Prod,
              unless Cloud AI PII redaction is enabled (Admin &gt; Security).
              When PII redaction is ON, cloud AI is allowed
              on the private path with PII removed before the AI call.
            </p>
          </div>
        </div>
      </div>

      {/* Path x Profile matrix */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {PROFILES.map((profile) => (
          <div key={profile} className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
              {PROFILE_LABELS[profile]}
            </h3>
            {PATHS.map((path) => {
              const key = `${path}:${profile}`;
              const cfg = getConfig(path, profile);
              const isEditing = editing === key;
              const isSaving = saving === key;
              const justSaved = savedKey === key;
              const form = isEditing && editForm
                ? editForm
                : { backend_type: cfg?.backend_type ?? 'local', model_name: cfg?.model_name ?? '', endpoint: cfg?.endpoint ?? '' };
              // the private path hosted-model check is now a soft
              // warning (amber banner below), not a hard block. The backend
              // returns guard_rail_warning on save; we surface it as a toast.

              return (
                <div
                  key={key}
                  className="card p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                        path === 'public' ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-700'
                      }`}>
                        {PATH_LABELS[path]}
                      </span>
                    </div>
                    {!isEditing && (
                      <button
                        onClick={() => startEdit(path, profile)}
                        className="text-slate-400 hover:text-primary-600 transition-colors"
                        aria-label={`Edit ${PATH_LABELS[path]} ${PROFILE_LABELS[profile]} config`}
                      >
                        <Edit2 size={16} />
                      </button>
                    )}
                  </div>

                  {/* soft warning when the private path targets a
                      hosted/cloud model that is not marked self-hosted. Shown
                      in both the edit + read-only views. */}
                  {((isEditing && editForm)
                    ? isPrivateHostedWarning(editForm.path, editForm.backend_type, editForm.self_hosted)
                    : isPrivateHostedWarning(path, cfg?.backend_type ?? 'local', cfg?.self_hosted)) && (
                    <div className="flex items-start gap-2 mb-3 p-2 rounded-lg bg-amber-50 border border-amber-300">
                      <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={16} />
                      <p className="text-xs font-medium text-amber-800">
                        Private documents are set to use a hosted AI. Document text will be sent to the cloud AI. Enable &ldquo;Cloud AI PII redaction&rdquo; in Security to scrub PII before the call.
                      </p>
                    </div>
                  )}

                  {isEditing && editForm ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">
                          {ADMIN_LABELS.backendType}
                        </label>
                        <select
                          className="w-full text-sm border border-slate-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none"
                          value={editForm.backend_type}
                          onChange={(e) => {
                            const bt = e.target.value as 'cloud' | 'local' | 'anthropic' | 'claude_code' | 'pi_agent';
                            // Prepopulate sensible defaults when the backend type changes
                            // (operator UX: don't make them type the endpoint/model each time).
                            // Anthropic -> official API base + default Claude model.
                            // Local -> qwen2.5:7b (the DR local model of record).
                            // claude_code / pi_agent -> agentic bridge URL + claude-sonnet-5.
                            // Cloud -> leave endpoint/model as-is (varies by provider).
                            const presets: Record<string, { endpoint?: string; model_name?: string }> = {
                              anthropic: { endpoint: 'https://api.anthropic.com', model_name: 'claude-sonnet-5' },
                              local: { model_name: 'qwen2.5:7b' },
                              openai: { model_name: 'Qwen/Qwen2.5-7B-Instruct-AWQ' },
                              claude_code: { endpoint: 'http://agentic-bridge:8787', model_name: 'claude-sonnet-5' },
                              pi_agent: { endpoint: 'http://agentic-bridge:8787', model_name: 'claude-sonnet-5' },
                            };
                            const preset = presets[bt] || {};
                            setEditForm({
                              ...editForm,
                              backend_type: bt,
                              endpoint: preset.endpoint !== undefined ? preset.endpoint : (editForm.endpoint ?? ''),
                              model_name: preset.model_name !== undefined ? preset.model_name : (editForm.model_name ?? ''),
                            });
                          }}
                        >
                          <option value="local">{ADMIN_LABELS.local}</option>
                          <option value="openai">{ADMIN_LABELS.openai}</option>
                          <option value="cloud">{ADMIN_LABELS.cloud}</option>
                          <option value="anthropic">{ADMIN_LABELS.anthropic}</option>
                          <option value="claude_code">{ADMIN_LABELS.claudeCode}</option>
                          {/* pi_agent hidden from the UI (operator 2026-09-19): blocked by Anthropic
                              policy -- third-party apps draw from paid 'extra usage', not the plan.
                              Backend support kept so an existing config still renders; re-enable
                              the option when extra-usage is enabled or a key is provided. */}
                        </select>
                        {editForm.backend_type === 'anthropic' && (
                          <p className="mt-1 text-xs text-amber-700">
                            {ADMIN_LABELS.anthropicNote}
                          </p>
                        )}
                        {isAgenticBackendType(editForm.backend_type) && (
                          <p className="mt-1 text-xs text-amber-700">
                            {ADMIN_LABELS.agenticNote}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">
                          {ADMIN_LABELS.modelName}
                        </label>
                        <input
                          type="text"
                          className="w-full text-sm border border-slate-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none"
                          value={editForm.model_name}
                          onChange={(e) => setEditForm({ ...editForm, model_name: e.target.value })}
                          placeholder="e.g. model-name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">
                          {isAgenticBackendType(editForm.backend_type) ? ADMIN_LABELS.bridgeUrl : 'Endpoint (optional)'}
                        </label>
                        <input
                          type="text"
                          className="w-full text-sm border border-slate-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none"
                          value={editForm.endpoint ?? ''}
                          onChange={(e) => setEditForm({ ...editForm, endpoint: e.target.value })}
                          placeholder={isAgenticBackendType(editForm.backend_type) ? ADMIN_LABELS.bridgeUrlPlaceholder : 'https://...'}
                        />
                      </div>
                      {isCloudBackendType(editForm.backend_type) && editForm.path === 'private' && (
                        <div className="flex items-start gap-2 p-2 rounded-lg bg-slate-50 border border-slate-200">
                          <input
                            type="checkbox"
                            id={`selfhosted-${editForm.path}-${editForm.profile}`}
                            checked={editForm.self_hosted ?? false}
                            onChange={(e) => setEditForm({ ...editForm, self_hosted: e.target.checked })}
                            className="mt-0.5"
                          />
                          <label htmlFor={`selfhosted-${editForm.path}-${editForm.profile}`} className="text-xs text-slate-600 cursor-pointer select-none">
                            <span className="font-medium text-slate-700">Self-hosted / private cloud</span>
                            <br />
                            The endpoint is on our own infrastructure. The security policy allows this on the private path without PII redaction, because the data never leaves our network.
                          </label>
                        </div>
                      )}
                      {(editForm.backend_type === 'anthropic' || editForm.backend_type === 'cloud') && (
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">
                            API Key
                          </label>
                          {cfg?.has_api_key && cfg.api_key_masked && !keyEditing && !editForm.clear_api_key ? (
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-slate-600 font-mono bg-slate-100 px-2 py-1.5 rounded-md border border-slate-200">
                                {cfg.api_key_masked}
                              </span>
                              <button
                                type="button"
                                onClick={() => setKeyEditing(true)}
                                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                              >
                                Change key
                              </button>
                              <button
                                type="button"
                                onClick={() => { setEditForm({ ...editForm, clear_api_key: true, api_key: undefined }); setKeyEditing(false); }}
                                className="text-xs text-red-600 hover:text-red-700 font-medium"
                              >
                                Remove
                              </button>
                            </div>
                          ) : editForm.clear_api_key ? (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1.5">
                                API key will be removed when you click Save.
                              </span>
                              <button
                                type="button"
                                onClick={() => setEditForm({ ...editForm, clear_api_key: false })}
                                className="text-xs text-slate-600 hover:text-slate-800 font-medium underline"
                              >
                                Undo
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <input
                                type="password"
                                className="flex-1 text-sm border border-slate-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none"
                                value={editForm.api_key ?? ''}
                                onChange={(e) => setEditForm({ ...editForm, api_key: e.target.value, clear_api_key: false })}
                                placeholder="Paste your API key here"
                                autoComplete="off"
                              />
                              {keyEditing && cfg?.has_api_key && (
                                <button
                                  type="button"
                                  onClick={() => { setKeyEditing(false); setEditForm({ ...editForm, api_key: undefined }); }}
                                  className="text-xs text-slate-500 hover:text-slate-700 font-medium underline shrink-0"
                                >
                                  Cancel change
                                </button>
                              )}
                            </div>
                          )}
                          <p className="mt-1 text-[11px] text-slate-400">
                            Paste the actual secret key. It is encrypted before storage and never shown in full again. This is all you need.
                          </p>
                        </div>
                      )}
                      {(editForm.backend_type === 'anthropic' || editForm.backend_type === 'cloud') && (
                        <details className="mt-1">
                          <summary className="text-xs text-slate-500 cursor-pointer select-none hover:text-slate-700">
                            Advanced: reference an env var instead of pasting the key
                          </summary>
                          <div className="mt-2">
                            <label className="block text-xs font-medium text-slate-500 mb-1">
                              {ADMIN_LABELS.apiKeyRef}
                            </label>
                            <input
                              type="text"
                              className="w-full text-sm border border-slate-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none"
                              value={editForm.api_key_ref ?? ''}
                              onChange={(e) => setEditForm({ ...editForm, api_key_ref: e.target.value })}
                              placeholder={editForm.backend_type === 'anthropic' ? 'ANTHROPIC_API_KEY' : 'e.g. OPENAI_API_KEY'}
                            />
                            <p className="mt-1 text-[11px] text-slate-400">
                              Optional. Only use this if your key lives in an environment variable (e.g. ANTHROPIC_API_KEY) and you prefer not to paste it. Most users leave this blank.
                            </p>
                          </div>
                        </details>
                      )}
                      <div className="flex items-center gap-2 pt-1">
                        <button
                          onClick={handleSave}
                          disabled={isSaving || !editForm.model_name.trim()}
                          className="btn-primary text-sm px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSaving ? (
                            <Loader2 className="animate-spin" size={14} />
                          ) : justSaved ? (
                            <CheckCircle2 size={14} />
                          ) : (
                            <Save size={14} />
                          )}
                          {isSaving ? ADMIN_LABELS.saving : ADMIN_LABELS.save}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="btn-secondary text-sm px-3 py-1.5"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleTestConfig(editForm.path, editForm.profile)}
                          disabled={testingKey === key || !editForm.model_name.trim()}
                          className="btn-secondary text-sm px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Test this configuration with a tiny AI ping"
                        >
                          {testingKey === key ? (
                            <Loader2 className="animate-spin" size={14} />
                          ) : (
                            <Zap size={14} />
                          )}
                          Test
                        </button>
                      </div>
                      {testResults[key] && editing === key && (
                        <div className={`text-xs px-2 py-1.5 rounded-md border ${testResults[key].ok ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                          {testResults[key].ok ? '✓ ' : '✗ '}{testResults[key].message}
                          {testResults[key].latency_ms != null ? ` (${testResults[key].latency_ms}ms)` : ''}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        {form.backend_type === 'cloud' ? (
                          <Cloud className="text-blue-500" size={16} />
                        ) : form.backend_type === 'anthropic' ? (
                          <Cloud className="text-indigo-500" size={16} />
                        ) : form.backend_type === 'claude_code' || form.backend_type === 'pi_agent' ? (
                          <Sparkles className="text-purple-500" size={16} />
                        ) : (
                          <Server className="text-green-500" size={16} />
                        )}
                        <span className="text-sm font-medium text-slate-700">
                          {form.backend_type === 'anthropic'
                            ? ADMIN_LABELS.anthropic
                            : form.backend_type === 'claude_code'
                              ? ADMIN_LABELS.claudeCode
                              : form.backend_type === 'pi_agent'
                                ? ADMIN_LABELS.piAgent
                                : form.backend_type === 'cloud'
                                  ? ADMIN_LABELS.cloud
                                  : ADMIN_LABELS.local}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 truncate" title={form.model_name}>
                        {form.model_name || <span className="text-slate-400 italic">Not configured</span>}
                      </p>
                      {cfg?.endpoint && (
                        <p className="text-xs text-slate-400 truncate" title={cfg.endpoint}>
                          {cfg.endpoint}
                        </p>
                      )}
                      {justSaved && (
                        <div className="flex items-center gap-1 text-green-600 text-xs">
                          <CheckCircle2 size={12} />
                          {ADMIN_LABELS.saved}
                        </div>
                      )}
                      {form.model_name && (
                        <div className="flex items-center gap-2 pt-1">
                          <button
                            onClick={() => handleTestConfig(path, profile)}
                            disabled={testingKey === key}
                            className="btn-secondary text-xs px-2.5 py-1 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Test this configuration with a tiny AI ping"
                          >
                            {testingKey === key ? (
                              <Loader2 className="animate-spin" size={12} />
                            ) : (
                              <Zap size={12} />
                            )}
                            Test
                          </button>
                          {testResults[key] && editing !== key && (
                            <span className={`text-xs ${testResults[key].ok ? 'text-green-600' : 'text-red-600'}`}>
                              {testResults[key].ok ? '✓' : '✗'} {testResults[key].message}
                              {testResults[key].latency_ms != null ? ` (${testResults[key].latency_ms}ms)` : ''}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   Data Sources Tab
   ============================================================ */
const FALLBACK_SOURCES: DataSource[] = [
  { id: 'edgar', source: 'EDGAR', display_name: 'SEC EDGAR', description: 'SEC filings, delinquency, CIK lookup', source_url: 'https://www.sec.gov/edgar', egress_enabled: true },
  { id: 'otc_markets', source: 'OTC_MARKETS', display_name: 'OTC Markets', description: 'OTC tier, shell status, S/O, float, splits', source_url: 'https://www.otcmarkets.com', egress_enabled: true },
  { id: 'yahoo_finance', source: 'YAHOO_FINANCE', display_name: 'Yahoo Finance', description: 'Quote, volume, price history', source_url: 'https://finance.yahoo.com', egress_enabled: true },
  { id: 'promo', source: 'PROMO', display_name: 'Promo Monitoring', description: 'Promotional activity detection', source_url: '', egress_enabled: false },
];

const SOURCE_ICONS: Record<string, string> = {
  EDGAR: '\u{1F4CB}',
  OTC_MARKETS: '\u{1F4C8}',
  YAHOO_FINANCE: '\u{1F4B9}',
  PROMO: '\u{1F4E2}',
  FINRA: '\u{1F3DB}',
};

function DataSourcesTab() {
  const { addToast } = useToastStore();
  const [sources, setSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);

  const loadSources = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getDataSources();
      setSources(data);
    } catch {
      // API endpoint may not exist yet; use fallback defaults
      setSources(FALLBACK_SOURCES);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSources(); }, [loadSources]);

  const handleToggle = async (source: DataSource) => {
    setToggling(source.id);
    const newEnabled = !source.egress_enabled;
    try {
      const updated = await toggleDataSource(source.id, newEnabled);
      setSources((prev) => prev.map((s) => (s.id === source.id ? updated : s)));
      addToast(ADMIN_LABELS.egressUpdated, 'success');
    } catch {
      // Optimistic update if API not ready
      setSources((prev) => prev.map((s) => (s.id === source.id ? { ...s, egress_enabled: newEnabled } : s)));
      addToast(ADMIN_LABELS.egressUpdated, 'success');
    } finally {
      setToggling(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-primary-500" size={28} />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {sources.map((source) => (
        <div key={source.id} className="card p-5 card-hover">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-xl">
                {SOURCE_ICONS[source.source] ?? '\u{1F4C2}'}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-800">{source.display_name}</h3>
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                  source.egress_enabled
                    ? 'bg-green-100 text-green-700'
                    : 'bg-slate-200 text-slate-500'
                }`}>
                  {source.egress_enabled ? ADMIN_LABELS.egressEnabled : ADMIN_LABELS.egressDisabled}
                </span>
                {source.per_source_toggleable === false && (
                  <span className="text-[10px] text-slate-400 ml-1">(global)</span>
                )}
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-500 mb-3 min-h-[2.5rem]">{source.description}</p>

          {source.source_url && (
            <a
              href={source.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary-600 hover:text-primary-700 hover:underline block mb-4 truncate"
              title={source.source_url}
            >
              {source.source_url}
            </a>
          )}

          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <span className="text-xs font-medium text-slate-600">{ADMIN_LABELS.egressStatus}</span>
            <button
              onClick={() => source.per_source_toggleable !== false && handleToggle(source)}
              disabled={toggling === source.id || source.per_source_toggleable === false}
              title={source.per_source_toggleable === false ? 'Governed by the global egress switch' : ''}
              className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 disabled:opacity-50"
              style={{
                backgroundColor: source.egress_enabled ? '#16a34a' : '#cbd5e1',
              }}
              role="switch"
              aria-checked={source.egress_enabled}
              aria-label={`${ADMIN_LABELS.toggleEgress} for ${source.display_name}`}
            >
              {toggling === source.id ? (
                <Loader2 className="absolute left-1 animate-spin text-white" size={14} />
              ) : (
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    source.egress_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              )}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ============================================================
   Users Tab
   ============================================================ */
function UsersTab() {
  const { addToast } = useToastStore();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [saving, setSaving] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminUsers();
      setUsers(data);
    } catch {
      addToast('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleCreate = async (input: CreateUserInput) => {
    setSaving(true);
    try {
      await createUser(input);
      await loadUsers();
      addToast(ADMIN_LABELS.userCreated, 'success');
      setShowCreate(false);
    } catch {
      addToast('Failed to create user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (userId: string, input: { full_name?: string; role?: AdminUser['role']; is_active?: boolean; can_see_all_cases?: boolean }) => {
    setSaving(true);
    try {
      await updateUser(userId, input);
      await loadUsers();
      addToast(ADMIN_LABELS.userUpdated, 'success');
      setEditingUser(null);
    } catch {
      addToast('Failed to update user', 'error');
    } finally {
      setSaving(false);
    }
  };

  // toggle per-user learned-rule training capability. Operators
  // always can (via role) and are not editable here; other roles are
  // toggled by merging can_train_rules into the existing settings bag.
  const handleToggleCanTrain = async (user: AdminUser, next: boolean) => {
    const existing = user.settings ?? {};
    const merged = { ...existing, can_train_rules: next };
    setSaving(true);
    try {
      await updateUser(user.id, { settings: merged });
      await loadUsers();
      addToast(`Training ${next ? 'enabled' : 'disabled'} for ${user.full_name}`, 'success');
    } catch {
      addToast('Failed to update training flag', 'error');
    } finally {
      setSaving(false);
    }
  };

  // toggle per-user case-chat capability. Operators always can
  // (via role) and are not editable here; other roles are toggled by
  // merging can_chat_case into the existing settings bag.
  const handleToggleCanChat = async (user: AdminUser, next: boolean) => {
    const existing = user.settings ?? {};
    const merged = { ...existing, can_chat_case: next };
    setSaving(true);
    try {
      await updateUser(user.id, { settings: merged });
      await loadUsers();
      addToast(`Chat ${next ? 'enabled' : 'disabled'} for ${user.full_name}`, 'success');
    } catch {
      addToast('Failed to update chat flag', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-primary-500" size={28} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">{users.length} user{users.length !== 1 ? 's' : ''}</p>
        <button
          onClick={() => setShowCreate(true)}
          className="btn-primary text-sm px-4 py-2 flex items-center gap-1.5"
        >
          <Plus size={16} />
          {ADMIN_LABELS.createUser}
        </button>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left">
                <th className="px-4 py-3 font-semibold text-slate-600">{ADMIN_LABELS.fullName}</th>
                <th className="px-4 py-3 font-semibold text-slate-600">{ADMIN_LABELS.email}</th>
                <th className="px-4 py-3 font-semibold text-slate-600">{ADMIN_LABELS.role}</th>
                <th className="px-4 py-3 font-semibold text-slate-600">{ADMIN_LABELS.status}</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Can train rules</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Can chat</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 text-slate-800 font-medium">{user.full_name}</td>
                  <td className="px-4 py-3 text-slate-600">{user.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${ROLE_BADGE_CLASSES[user.role] ?? 'bg-slate-100 text-slate-600'}`}>
                      {ROLE_LABELS[user.role] ?? user.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                      user.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'
                    }`}>
                      {user.is_active ? ADMIN_LABELS.activeStatus : ADMIN_LABELS.inactiveStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {user.role === 'operator' ? (
                      <div className="flex items-center gap-2">
                        <ToggleSwitch checked disabled />
                        <span className="text-xs text-slate-400">via role</span>
                      </div>
                    ) : (
                      <ToggleSwitch
                        checked={!!(user.settings?.can_train_rules)}
                        disabled={saving}
                        onChange={(next) => handleToggleCanTrain(user, next)}
                      />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {user.role === 'operator' ? (
                      <div className="flex items-center gap-2">
                        <ToggleSwitch checked disabled />
                        <span className="text-xs text-slate-400">via role</span>
                      </div>
                    ) : (
                      <ToggleSwitch
                        checked={!!(user.settings?.can_chat_case)}
                        disabled={saving}
                        onChange={(next) => handleToggleCanChat(user, next)}
                      />
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setEditingUser(user)}
                      className="text-slate-400 hover:text-primary-600 transition-colors"
                      aria-label={`Edit ${user.full_name}`}
                    >
                      <Edit2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                    {ADMIN_LABELS.noUsers}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <UserModal
          title={ADMIN_LABELS.createUser}
          onClose={() => setShowCreate(false)}
          onSave={(input) => handleCreate(input as CreateUserInput)}
          saving={saving}
          isCreate
        />
      )}
      {editingUser && (
        <UserModal
          title={ADMIN_LABELS.editUser}
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={(input) => handleUpdate(editingUser.id, input as { full_name?: string; role?: AdminUser['role']; is_active?: boolean })}
          saving={saving}
        />
      )}
    </div>
  );
}

/* ---------- User Modal ---------- */
function UserModal({
  title, user, onClose, onSave, saving, isCreate,
}: {
  title: string;
  user?: AdminUser;
  onClose: () => void;
  onSave: (input: CreateUserInput | { full_name?: string; role?: AdminUser['role']; is_active?: boolean; can_see_all_cases?: boolean }) => void;
  saving: boolean;
  isCreate?: boolean;
}) {
  const [fullName, setFullName] = useState(user?.full_name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [role, setRole] = useState(user?.role ?? 'analyst');
  const [password, setPassword] = useState('');
  const [isActive, setIsActive] = useState(user?.is_active ?? true);
  const [canSeeAll, setCanSeeAll] = useState(user?.can_see_all_cases ?? false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isCreate) {
      onSave({ email, full_name: fullName, role: role as CreateUserInput['role'], password, can_see_all_cases: canSeeAll });
    } else {
      onSave({ full_name: fullName, role, is_active: isActive, can_see_all_cases: canSeeAll });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div className="card p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">{ADMIN_LABELS.fullName}</label>
            <input
              type="text"
              required
              className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">{ADMIN_LABELS.email}</label>
            <input
              type="email"
              required
              disabled={!isCreate}
              className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none disabled:bg-slate-50 disabled:text-slate-400"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">{ADMIN_LABELS.role}</label>
            <select
              className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none"
              value={role}
              onChange={(e) => setRole(e.target.value as AdminUser['role'])}
            >
              <option value="analyst">Analyst</option>
              <option value="reviewer">Reviewer</option>
              <option value="principal">Principal</option>
              <option value="compliance">Compliance</option>
              <option value="operator">Operator</option>
            </select>
          </div>
          {isCreate && (
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Password</label>
              <input
                type="password"
                required
                className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          )}
          {!isCreate && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="rounded border-slate-300 text-primary-600 focus:ring-primary-400"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              <span className="text-sm text-slate-600">{ADMIN_LABELS.activeStatus}</span>
            </label>
          )}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="rounded border-slate-300 text-primary-600 focus:ring-primary-400"
              checked={canSeeAll}
              onChange={(e) => setCanSeeAll(e.target.checked)}
            />
            <span className="text-sm text-slate-600">Can see all cases (oversight visibility)</span>
          </label>
          <div className="flex items-center gap-2 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="btn-primary text-sm px-4 py-2 flex items-center gap-1.5 disabled:opacity-50"
            >
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              {saving ? ADMIN_LABELS.saving : ADMIN_LABELS.save}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary text-sm px-4 py-2">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ============================================================
   Egress Log Tab ( compliance audit)
   ============================================================ */

/* ============================================================
   Workflows Tab
   ============================================================ */
function WorkflowsTab() {
  const { addToast } = useToastStore();
  const [workflows, setWorkflows] = useState<WorkflowListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [stepName, setStepName] = useState<Record<string, string>>({});
  const [stepRole, setStepRole] = useState<Record<string, string>>({});
  const [stepOrder] = useState<Record<string, number>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getWorkflows();
      setWorkflows(data);
    } catch {
      addToast('Failed to load workflows', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await createWorkflow({ name: newName });
      setNewName('');
      addToast('Workflow created', 'success');
      load();
    } catch {
      addToast('Failed to create workflow', 'error');
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await updateWorkflow(id, { is_default: true });
      addToast('Default workflow set', 'success');
      load();
    } catch {
      addToast('Failed to set default', 'error');
    }
  };

  const handleAddStep = async (wfId: string) => {
    const name = stepName[wfId]?.trim();
    const role = stepRole[wfId] || 'reviewer';
    const order = stepOrder[wfId] ?? (workflows.find((w) => w.id === wfId)?.steps.length || 0) + 1;
    if (!name) {
      addToast('Step name required', 'error');
      return;
    }
    try {
      await addWorkflowStep(wfId, { order, name, required_role: role as 'reviewer', required_approvals: 1 });
      setStepName({ ...stepName, [wfId]: '' });
      addToast('Step added', 'success');
      load();
    } catch {
      addToast('Failed to add step', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-primary-500" size={28} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="card p-4 flex items-center gap-2">
        <input
          type="text"
          placeholder="New workflow name..."
          className="input flex-1"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <button onClick={handleCreate} className="btn-primary text-sm flex items-center gap-1">
          <Plus size={16} /> Create workflow
        </button>
      </div>

      {workflows.map((wf) => (
        <div key={wf.id} className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">{wf.name}</h3>
              <p className="text-xs text-slate-400">
                {wf.is_default ? 'Default workflow' : 'Optional'} -
                {' '}{wf.is_active ? 'Active' : 'Inactive'}
              </p>
            </div>
            {!wf.is_default && (
              <button
                onClick={() => handleSetDefault(wf.id)}
                className="btn-secondary text-xs"
              >
                Set as default
              </button>
            )}
          </div>

          {wf.steps.length > 0 ? (
            <div className="space-y-1 mb-3">
              {wf.steps.sort((a, b) => a.order - b.order).map((s) => (
                <div key={s.id} className="flex items-center gap-2 text-sm text-slate-600 p-2 rounded bg-slate-50">
                  <span className="text-xs font-mono text-slate-400">{s.order}</span>
                  <span className="font-medium">{s.name}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-slate-200 text-slate-600">
                    {s.required_role}
                  </span>
                  <span className="text-xs text-slate-400">
                    approvals: {s.required_approvals}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400 mb-3">No steps configured.</p>
          )}

          <div className="flex items-center gap-2 border-t border-slate-100 pt-3">
            <input
              type="text"
              placeholder="Step name..."
              className="input flex-1 text-sm"
              value={stepName[wf.id] || ''}
              onChange={(e) => setStepName({ ...stepName, [wf.id]: e.target.value })}
            />
            <select
              className="input text-sm w-32"
              value={stepRole[wf.id] || 'reviewer'}
              onChange={(e) => setStepRole({ ...stepRole, [wf.id]: e.target.value })}
            >
              <option value="analyst">Analyst</option>
              <option value="reviewer">Reviewer</option>
              <option value="principal">Principal</option>
              <option value="compliance">Compliance</option>
            </select>
            <button onClick={() => handleAddStep(wf.id)} className="btn-secondary text-xs flex items-center gap-1">
              <Plus size={14} /> Add step
            </button>
          </div>
        </div>
      ))}
      {workflows.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-8">No workflows configured.</p>
      )}
    </div>
  );
}

const EGRESS_CATEGORIES = ['', 'public_source', 'web_search', 'llm', 'screenshot'];

function formatBytes(n: number | null): string {
  if (n === null || n === undefined) return '-';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

// small accessible toggle used in the Users tab.
function ToggleSwitch({
  checked, onChange, disabled, label,
}: {
  checked: boolean;
  onChange?: (next: boolean) => void;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        checked ? 'bg-green-600' : 'bg-slate-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

function formatTs(ts: string): string {
  try {
    const d = new Date(ts);
    return d.toLocaleString();
  } catch {
    return ts;
  }
}

function EgressLogTab() {
  const { addToast } = useToastStore();
  const [entries, setEntries] = useState<EgressLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [filters, setFilters] = useState<EgressLogQuery>({
    category: '', destination_host: '', case_id: '', since: '', q: '', sensitive: undefined,
  });
  const [applied, setApplied] = useState<EgressLogQuery>({});
  const [expanded, setExpanded] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});

  const load = useCallback(async (q: EgressLogQuery, p: number) => {
    setLoading(true);
    try {
      const clean: EgressLogQuery = { ...q, page: p, limit };
      for (const k of Object.keys(clean) as (keyof EgressLogQuery)[]) {
        const v = clean[k];
        if (v === undefined || v === null || v === '') delete (clean as Record<string, unknown>)[k as string];
      }
      const data = await getEgressLog(clean);
      setEntries(data.items);
      setTotal(data.total);
    } catch {
      addToast('Failed to load egress log', 'error');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [addToast, limit]);

  useEffect(() => { load(applied, page); }, [load, applied, page]);

  const applyFilters = () => {
    setPage(1);
    setApplied({ ...filters });
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const categoryBadge: Record<string, string> = {
    public_source: 'bg-blue-100 text-blue-800',
    web_search: 'bg-purple-100 text-purple-800',
    llm: 'bg-amber-100 text-amber-800',
    screenshot: 'bg-teal-100 text-teal-800',
  };

  return (
    <div>
      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 mb-5 p-4 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex flex-col">
          <label className="text-xs font-medium text-slate-500 mb-1">Category</label>
          <select
            className="input text-sm"
            value={filters.category || ''}
            onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
          >
            {EGRESS_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c || 'All'}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-xs font-medium text-slate-500 mb-1">Destination host</label>
          <input
            className="input text-sm"
            placeholder="e.g. www.sec.gov"
            value={filters.destination_host || ''}
            onChange={(e) => setFilters((f) => ({ ...f, destination_host: e.target.value }))}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs font-medium text-slate-500 mb-1">Case ID</label>
          <input
            className="input text-sm"
            placeholder="case uuid"
            value={filters.case_id || ''}
            onChange={(e) => setFilters((f) => ({ ...f, case_id: e.target.value }))}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs font-medium text-slate-500 mb-1">Since</label>
          <input
            type="datetime-local"
            className="input text-sm"
            value={filters.since || ''}
            onChange={(e) => setFilters((f) => ({ ...f, since: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs font-medium text-slate-500 mb-1">Sensitive</label>
          <select
            className="input text-sm"
            value={filters.sensitive === undefined ? '' : String(filters.sensitive)}
            onChange={(e) => setFilters((f) => ({ ...f, sensitive: e.target.value === '' ? undefined : e.target.value === 'true' }))}
          >
            <option value="">All</option>
            <option value="true">Sensitive only</option>
            <option value="false">Non-sensitive only</option>
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-xs font-medium text-slate-500 mb-1">Search</label>
          <input
            className="input text-sm"
            placeholder="URL / payload substring"
            value={filters.q || ''}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          />
        </div>
        <button className="btn-primary text-sm px-4 py-2" onClick={applyFilters}>Apply</button>
        <button
          className="btn-secondary text-sm px-4 py-2"
          onClick={() => {
            const empty = { category: '', destination_host: '', case_id: '', since: '', q: '', sensitive: undefined };
            setFilters(empty);
            setApplied({});
            setPage(1);
          }}
        >Clear</button>
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">{total} entr{total === 1 ? 'y' : 'ies'} (newest first)</p>
        <button className="text-sm text-primary-600 hover:underline" onClick={() => load(applied, page)}>
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="animate-spin text-primary-500" size={28} />
        </div>
      ) : entries.length === 0 ? (
        <p className="text-sm text-slate-500 py-12 text-center">No egress-log entries.</p>
      ) : (
        <>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border border-slate-200 rounded-lg">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Timestamp</th>
                <th className="px-3 py-2 text-left font-medium">Category</th>
                <th className="px-3 py-2 text-left font-medium">Destination</th>
                <th className="px-3 py-2 text-left font-medium">Method</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-left font-medium">Success</th>
                <th className="px-3 py-2 text-left font-medium">Duration</th>
                <th className="px-3 py-2 text-left font-medium">Source</th>
                <th className="px-3 py-2 text-left font-medium">Case</th>
                <th className="px-3 py-2 text-left font-medium">Flags</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entries.map((e) => {
                const isOpen = expanded === e.id;
                const isRevealed = !!revealed[e.id];
                return (
                  <React.Fragment key={e.id}>
                    <tr
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => setExpanded(isOpen ? null : e.id)}
                    >
                      <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{formatTs(e.ts)}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${categoryBadge[e.category] || 'bg-slate-100 text-slate-600'}`}> {e.category} </span>
                      </td>
                      <td className="px-3 py-2 text-slate-700 max-w-[16rem] truncate" title={e.destination_host}>{e.destination_host}</td>
                      <td className="px-3 py-2 text-slate-600">{e.method}</td>
                      <td className="px-3 py-2 text-slate-600">{e.response_status ?? '-'}</td>
                      <td className="px-3 py-2">
                        {e.success ? (
                          <CheckCircle2 className="text-green-600" size={16} />
                        ) : (
                          <X className="text-red-500" size={16} />
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{e.duration_ms != null ? `${e.duration_ms} ms` : '-'}</td>
                      <td className="px-3 py-2 text-slate-600 text-xs">{e.source_module}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs font-mono">{e.case_id ? e.case_id.slice(0, 8) : '-'}</td>
                      <td className="px-3 py-2">
                        {e.sensitive && (
                          <span className="text-xs font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-800">sensitive</span>
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50">
                        <td colSpan={10} className="px-4 py-3">
                          <div className="text-xs text-slate-500 mb-1"><strong>URL:</strong> <span className="text-slate-700 break-all">{e.destination_url}</span></div>
                          <div className="text-xs text-slate-500 mb-1">
                            <strong>Response size:</strong> {formatBytes(e.response_size)}
                            {e.error && (<span className="ml-3"><strong>Error:</strong> <span className="text-red-600">{e.error}</span></span>)}
                          </div>
                          <div className="mt-2">
                            <div className="flex items-center gap-2 mb-1">
                              <strong className="text-xs text-slate-600">Request payload:</strong>
                              {e.sensitive && (
                                <button
                                  className="text-xs px-2 py-0.5 rounded border border-slate-300 hover:bg-slate-100"
                                  onClick={(ev) => { ev.stopPropagation(); setRevealed((r) => ({ ...r, [e.id]: !r[e.id] })); }}
                                >
                                  {isRevealed ? 'Hide' : 'Reveal'}
                                </button>
                              )}
                            </div>
                            <pre className={`text-xs bg-white border border-slate-200 rounded p-3 overflow-auto max-h-72 max-w-full whitespace-pre-wrap break-words ${e.sensitive && !isRevealed ? 'blur-sm select-none' : ''}`}>{JSON.stringify(e.request_payload, null, 2)}</pre>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <button className="btn-secondary text-sm px-3 py-1.5" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
            <button className="btn-secondary text-sm px-3 py-1.5" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
          </div>
        </div>
        </>
      )}
    </div>
  );
}

/* ============================================================
   Main Admin Page
   ============================================================ */
const TABS: Array<{ key: Tab; label: string; icon: typeof Settings }> = [
  { key: 'rubric', label: ADMIN_LABELS.rubric, icon: ShieldCheck },
  { key: 'learnedRules', label: ADMIN_LABELS.learnedRules, icon: Sparkles },
  { key: 'rulesProfile', label: ADMIN_LABELS.rulesProfile, icon: FileText },
  { key: 'modelConfig', label: ADMIN_LABELS.modelConfig, icon: Settings },
  { key: 'dataSources', label: ADMIN_LABELS.dataSources, icon: Database },
  { key: 'users', label: ADMIN_LABELS.users, icon: Users },
  { key: 'workflows', label: 'Workflows', icon: Zap },
  { key: 'piiRules', label: 'PII Rules', icon: ShieldCheck },
  { key: 'egressLog', label: ADMIN_LABELS.egressLog, icon: ShieldCheck },
];

export default function Admin() {
  const { user } = useAuthStore();
  const isCompliance = user?.role === 'compliance';
  // the Workflow Manager is dev-only (hide in uat/prod/RC; tested in
  // dev first). Resolved from /health `profile` at runtime so a single bundle
  // gates it per environment.
  const [envProfile, setEnvProfile] = useState<string>('dev');
  useEffect(() => {
    const base = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL || '/api/v1';
    fetch(base + '/health')
      .then((r) => r.json())
      .then((d: { profile?: string }) => { if (d.profile) setEnvProfile(d.profile); })
      .catch(() => { /* best-effort: default dev keeps the tab visible */ });
  }, []);
  // Compliance role sees only the egressLog (Compliance Audit) tab; operator sees all.
  // Workflow Manager tab is dev-only.
  const tabs: Array<{ key: Tab; label: string; icon: typeof Settings }> = isCompliance
    ? TABS.filter((t) => t.key === 'egressLog')
    : TABS.filter((t) => t.key !== 'workflows' || envProfile === 'dev');
  const [activeTab, setActiveTab] = useState<Tab>(isCompliance ? 'egressLog' : 'rubric');

  // Role guard: operator (full admin) or compliance (egress-log only)
  if (!user || (user.role !== 'operator' && user.role !== 'compliance')) {
    return <Navigate to="/cases" replace />;
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Settings className="text-primary-600" size={24} />
        <h1 className="text-2xl font-bold text-slate-900">{ADMIN_LABELS.title}</h1>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 mb-6 border-b border-slate-200">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                isActive
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
              aria-selected={isActive}
              role="tab"
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div role="tabpanel">
        {activeTab === 'rubric' && <RubricTab />}
        {activeTab === 'learnedRules' && <LearnedRulesTab />}
        {activeTab === 'rulesProfile' && <RulesProfileTab />}
        {activeTab === 'modelConfig' && <ModelConfigTab />}
        {activeTab === 'dataSources' && <DataSourcesTab />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'workflows' && envProfile === 'dev' && <WorkflowsTab />}
        {activeTab === 'egressLog' && <EgressLogTab />}
        {activeTab === 'piiRules' && !isCompliance && <PiiRulesTab />}
      </div>
    </div>
  );
}